import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, KeyRound, ExternalLink, RefreshCw } from "lucide-react";
import { fetchProjectsAdminSupabase, fetchStatsSupabase, createProjectSupabase, updateProjectSupabase, deleteProjectSupabase } from "../lib/supabase";
import { clearToken } from "../lib/api";
import ProjectModal from "../components/ProjectModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import GalleryManager from "../components/GalleryManager";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ ongoing: 0, completed: 0, legacy: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // project object or {} for new
  const [showPwd, setShowPwd] = useState(false);
  const [fGroup, setFGroup] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState("projects");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([fetchProjectsAdminSupabase(), fetchStatsSupabase()]);
      setProjects(p);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logout = () => { clearToken(); nav("/admin"); };

  const handleSave = async (form) => {
    if (form.id) {
      await updateProjectSupabase(form.id, form);
    } else {
      await createProjectSupabase(form);
    }
    setEditing(null);
    await load();
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await deleteProjectSupabase(p.id);
    await load();
  };

  const toggleStatus = async (p) => {
    await updateProjectSupabase(p.id, { status: p.status === "ongoing" ? "completed" : "ongoing" });
    await load();
  };

  const filtered = useMemo(() => {
    let list = projects;
    if (fGroup !== "all") list = list.filter((p) => p.group === fGroup);
    if (fStatus !== "all") list = list.filter((p) => p.status === fStatus);
    if (q.trim()) {
      const t = q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(t) || (p.sector || "").toLowerCase().includes(t) || (p.location || "").toLowerCase().includes(t));
    }
    return list;
  }, [projects, fGroup, fStatus, q]);

  return (
    <div className="admin-shell">
      <div className="admin-bar">
        <div className="admin-bar-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img className="brand-logo" src="/assets/suvlogo.png" alt="SUV Engineers" />
            <span className="tag">Admin</span>
          </div>
          <div className="admin-actions">
            <a className="btn btn-ghost btn-sm" href="/" target="_blank" rel="noreferrer" data-testid="view-site-btn"><ExternalLink size={15} /> View Site</a>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowPwd(true)} data-testid="change-pwd-btn"><KeyRound size={15} /> Password</button>
            <button className="btn btn-copper btn-sm" onClick={logout} data-testid="logout-btn"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="admin-main">
        <div className="admin-stats">
          {[
            { k: "total", lbl: "Total Projects", v: stats.total, cls: "" },
            { k: "ongoing", lbl: "Ongoing", v: stats.ongoing, cls: "ongoing" },
            { k: "completed", lbl: "Completed", v: stats.completed, cls: "" },
            { k: "legacy", lbl: "Legacy", v: stats.legacy, cls: "" },
          ].map((s) => (
            <div key={s.k} className={`admin-stat ${s.cls}`} data-testid={`stat-${s.k}`}>
              <div className="num">{s.v}</div>
              <div className="lbl">{s.lbl}</div>
            </div>
          ))}
        </div>

        <div className="admin-view-tabs" data-testid="admin-view-tabs">
          <button className={`vtab ${view === "projects" ? "active" : ""}`} onClick={() => setView("projects")} data-testid="view-projects">Projects</button>
          <button className={`vtab ${view === "gallery" ? "active" : ""}`} onClick={() => setView("gallery")} data-testid="view-gallery">Gallery</button>
        </div>

        {view === "gallery" ? (
          <GalleryManager />
        ) : (
        <div className="admin-panel">
          <div className="admin-panel-head">
            <h2>Manage Projects</h2>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-ghost btn-sm" style={{ color: "var(--ink)", borderColor: "var(--line)", background: "#fff" }} onClick={load} data-testid="refresh-btn"><RefreshCw size={15} /> Refresh</button>
              <button className="btn btn-primary btn-sm" onClick={() => setEditing({})} data-testid="add-project-btn"><Plus size={16} /> Add Project</button>
            </div>
          </div>

          <div className="admin-filters">
            <select value={fGroup} onChange={(e) => setFGroup(e.target.value)} data-testid="filter-group">
              <option value="all">All Groups</option>
              <option value="current">Current</option>
              <option value="legacy">Legacy</option>
            </select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status">
              <option value="all">All Status</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
            </select>
            <input placeholder="Search projects…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
          </div>

          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">No projects found.</div>
          ) : (
            <table className="admin-table" data-testid="admin-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Sector</th>
                  <th>Group</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`row-${p.id}`}>
                    <td>
                      <div className="pname">{p.name}</div>
                      <div className="psub">{[p.client, p.location].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td>{p.sector}</td>
                    <td><span className={`tag-group ${p.group}`}>{p.group}</span></td>
                    <td>
                      <button
                        className={`status-badge ${p.status} status-toggle`}
                        onClick={() => toggleStatus(p)}
                        title="Click to toggle status"
                        data-testid={`toggle-status-${p.id}`}
                      >
                        <span className="dot" />{p.status === "ongoing" ? "Ongoing" : "Completed"}
                      </button>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="icon-btn" onClick={() => setEditing(p)} title="Edit" data-testid={`edit-${p.id}`}><Pencil /></button>
                        <button className="icon-btn danger" onClick={() => handleDelete(p)} title="Delete" data-testid={`delete-${p.id}`}><Trash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>

      {editing !== null && (
        <ProjectModal
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
}
